import { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { ProviderPermissionCard } from '@equip.health/ui';
import { useParams } from 'react-router-dom';

import { ProfileEventsAdmin } from '~/analytics/profileEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import {
  AdminRoles,
  AdminRolesMap,
  ProviderAccess,
} from '~/lib/constants/permissions.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { UserMode, useModeContext } from '~/lib/contexts/ModeContext';
import {
  getMaudPermissionDisplayValues,
  getOpsPermissionDisplayValues,
} from '~/lib/util/profile.util';

interface ProfilePermissionsProps {
  permissions: string[];
  roles: string[];
  providerType: string;
}

const ProfilePermissions = ({
  permissions,
  providerType,
  roles,
}: ProfilePermissionsProps) => {
  const params = useParams<UserProfileParams>();
  const providerExternalId = params.id ?? params[0]?.split('/')[0];
  const [showEditPermissionAccordion, setShowEditPermissionAccordion] =
    useState<boolean>(false);
  const { setComponentVisibility } = useComponentPortalContext();
  const { trackEvent } = useAnalytics();
  const { mode } = useModeContext();
  const { scriptsureTechOps: isScriptsureTechOpsEnabled } = useFlags<LDFlags>();
  useEffect(() => {
    if (showEditPermissionAccordion) {
      const data = {
        permissions,
        providerExternalId,
        providerType,
        roles,
      };
      setShowEditPermissionAccordion(false);
      setComponentVisibility({
        component: PortalComponent.EDIT_PERMISSIONS_ACCORDION,
        data,
        isExpanded: true,
        isVisible: true,
      });
    }
  }, [showEditPermissionAccordion]);

  const hasAdminAccess = permissions?.includes(ProviderAccess.ADMIN_ACCESS_APP);
  const hasMaudAccess = permissions?.includes(ProviderAccess.MAUD_ACCESS_APP);
  const hasOpsAccess = permissions?.includes(ProviderAccess.OPS_ACCESS_APP);

  const maudPermissions = getMaudPermissionDisplayValues(permissions);
  const opsPermissions = getOpsPermissionDisplayValues(permissions);

  const handlePermissionsEditClick = () => {
    setShowEditPermissionAccordion(true);
    if (mode === UserMode.ADMIN) {
      trackEvent(ProfileEventsAdmin.permissionsEditButtonClicked, {
        source: AnalyticsSourceValues.PROFILE_TAB_ADMIN,
      });
    }
  };
  const adminRoles =
    roles
      ?.filter((role: AdminRoles) =>
        [
          AdminRoles.ADMIN_MANAGE_USERS,
          AdminRoles.ADMIN_MANAGE_CONTENT,
        ].includes(role),
      )
      .map((role) => AdminRolesMap[role].display) || [];

  const adminPermissions = hasAdminAccess ? adminRoles : [];

  return (
    <ProviderPermissionCard
      adminPermissions={adminPermissions}
      handleEditClick={handlePermissionsEditClick}
      hasAdminAccess={hasAdminAccess}
      hasMaudAccess={hasMaudAccess}
      hasTechOpsAccess={hasOpsAccess}
      maudPermissions={maudPermissions}
      shouldShowTechOpsAccess={isScriptsureTechOpsEnabled}
      techOpsPermissions={opsPermissions}
    />
  );
};

export default ProfilePermissions;
